import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { DollarSign, RefreshCw, Save, Trash2, Edit2, X } from 'lucide-react';
import Layout from '../../components/Layout';
import { cobrancaAPI, clientesAPI } from '../../services/api';
import { useTheme } from '../../contexts/ThemeContext';

export default function AdminCobranca() {
  const { darkMode } = useTheme();
  const [clientes, setClientes] = useState([]);
  const [configs, setConfigs] = useState([]);
  const [cotacao, setCotacao] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editando, setEditando] = useState(null); // cliente_id sendo editado
  const [form, setForm] = useState({
    custo_marketing_usd: 0.0625,
    custo_utility_usd: 0.008,
    custo_authentication_usd: 0.0315,
    custo_service_usd: 0.03,
    taxa_dolar_pct: 10,
    iof_pct: 6.38,
    markup_pct: 0,
  });

  const card = `rounded-lg shadow-sm border ${darkMode ? 'bg-zinc-800 border-gray-700' : 'bg-white border-gray-200'}`;
  const textP = darkMode ? 'text-white' : 'text-gray-900';
  const textS = darkMode ? 'text-gray-400' : 'text-gray-500';
  const inputCls = `w-full px-3 py-2 rounded-lg border text-sm ${darkMode ? 'bg-zinc-700 border-zinc-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`;

  useEffect(() => { load(); }, []);

  const load = async () => {
    try {
      const [cotRes, cfgRes, cliRes] = await Promise.all([
        cobrancaAPI.getCotacao(),
        cobrancaAPI.getAllConfigs(),
        clientesAPI.list(),
      ]);
      setCotacao(cotRes.data.cotacao);
      setConfigs(cfgRes.data);
      setClientes(cliRes.data);
    } catch (e) {
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const atualizarCotacao = async () => {
    try {
      const res = await cobrancaAPI.getCotacao();
      setCotacao(res.data.cotacao);
      toast.success(`Cotação: R$ ${Number(res.data.cotacao).toFixed(4)}`);
    } catch (e) {
      toast.error('Erro ao atualizar cotação');
    }
  };

  const editarCliente = (clienteId) => {
    const cfg = configs.find(c => c.cliente_id === clienteId);
    if (cfg) {
      setForm({
        custo_marketing_usd: Number(cfg.custo_marketing_usd),
        custo_utility_usd: Number(cfg.custo_utility_usd),
        custo_authentication_usd: Number(cfg.custo_authentication_usd),
        custo_service_usd: Number(cfg.custo_service_usd),
        taxa_dolar_pct: Number(cfg.taxa_dolar_pct),
        iof_pct: Number(cfg.iof_pct),
        markup_pct: Number(cfg.markup_pct),
      });
    } else {
      setForm({
        custo_marketing_usd: 0.0625, custo_utility_usd: 0.008,
        custo_authentication_usd: 0.0315, custo_service_usd: 0.03,
        taxa_dolar_pct: 10, iof_pct: 6.38, markup_pct: 0,
      });
    }
    setEditando(clienteId);
  };

  const salvar = async () => {
    try {
      await cobrancaAPI.saveConfig(editando, form);
      toast.success('Configuração salva');
      setEditando(null);
      load();
    } catch (e) {
      toast.error('Erro ao salvar');
    }
  };

  const remover = async (cfg) => {
    try {
      await cobrancaAPI.deleteConfig(cfg.id);
      toast.success('Configuração removida');
      load();
    } catch (e) {
      toast.error('Erro ao remover');
    }
  };

  const getClienteNome = (id) => clientes.find(c => c.id === id)?.nome || id;
  const getConfig = (clienteId) => configs.find(c => c.cliente_id === clienteId);

  const calcDolarFinal = (cfg) => {
    if (!cotacao || !cfg) return 0;
    return cotacao * (1 + Number(cfg.taxa_dolar_pct) / 100) * (1 + Number(cfg.iof_pct) / 100);
  };

  const calcCustoMsg = (cfg, tipo = 'marketing') => {
    const dolar = calcDolarFinal(cfg);
    const custoMap = {
      marketing: Number(cfg.custo_marketing_usd),
      utility: Number(cfg.custo_utility_usd),
      authentication: Number(cfg.custo_authentication_usd),
      service: Number(cfg.custo_service_usd),
    };
    return custoMap[tipo] * dolar * (1 + Number(cfg.markup_pct) / 100);
  };

  if (loading) return <Layout><div className="flex justify-center py-20"><RefreshCw className="w-8 h-8 animate-spin text-green-500" /></div></Layout>;

  const clientesSemConfig = clientes.filter(c => !configs.find(cfg => cfg.cliente_id === c.id));

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className={`text-2xl font-bold ${textP}`}>💰 Cobrança por Mensagem</h1>
            <p className={textS}>Configure custos Meta, taxas e markup por cliente</p>
          </div>
          <div className="flex items-center gap-3">
            <div className={`px-4 py-2 rounded-lg ${darkMode ? 'bg-zinc-800 border border-zinc-700' : 'bg-white border border-gray-200'}`}>
              <span className={`text-sm ${textS}`}>Dólar: </span>
              <span className={`text-lg font-bold ${textP}`}>R$ {cotacao ? Number(cotacao).toFixed(4) : '...'}</span>
            </div>
            <button onClick={atualizarCotacao} className="p-2 rounded-lg bg-green-600 text-white hover:bg-green-700">
              <RefreshCw className="w-4 h-4" />
            </button>
            <a href="https://business.whatsapp.com/products/platform-pricing?country=Brasil&currency=D%C3%B3lar%20(USD)&category=Marketing"
              target="_blank" rel="noopener noreferrer"
              className={`px-3 py-2 rounded-lg text-sm ${darkMode ? 'bg-zinc-700 text-gray-300 hover:bg-zinc-600' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}>
              📋 Tabela Meta
            </a>
          </div>
        </div>

        {/* Clientes configurados */}
        {configs.map(cfg => (
          <div key={cfg.id} className={card}>
            <div className="p-5">
              <div className="flex items-center justify-between mb-4">
                <h3 className={`text-lg font-semibold ${textP}`}>{getClienteNome(cfg.cliente_id)}</h3>
                <div className="flex gap-2">
                  <button onClick={() => editarCliente(cfg.cliente_id)} className={`p-2 rounded-lg ${darkMode ? 'hover:bg-zinc-700' : 'hover:bg-gray-100'}`}>
                    <Edit2 className={`w-4 h-4 ${textS}`} />
                  </button>
                  <button onClick={() => remover(cfg)} className="p-2 rounded-lg hover:bg-red-100 dark:hover:bg-red-900/20">
                    <Trash2 className="w-4 h-4 text-red-500" />
                  </button>
                </div>
              </div>

              {editando === cfg.cliente_id ? renderForm() : (
                <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-4">
                  <Campo label="Marketing (USD)" valor={`$${Number(cfg.custo_marketing_usd).toFixed(4)}`} />
                  <Campo label="Utility (USD)" valor={`$${Number(cfg.custo_utility_usd).toFixed(4)}`} />
                  <Campo label="Auth (USD)" valor={`$${Number(cfg.custo_authentication_usd).toFixed(4)}`} />
                  <Campo label="Taxa Dólar" valor={`${Number(cfg.taxa_dolar_pct)}%`} />
                  <Campo label="IOF" valor={`${Number(cfg.iof_pct)}%`} />
                  <Campo label="Markup" valor={`${Number(cfg.markup_pct)}%`} />
                  <Campo label="Custo/msg (MKT)" valor={`R$ ${calcCustoMsg(cfg).toFixed(4)}`} destaque />
                </div>
              )}
            </div>
          </div>
        ))}

        {/* Adicionar novo cliente */}
        {clientesSemConfig.length > 0 && (
          <div className={card}>
            <div className="p-5">
              <h3 className={`text-lg font-semibold mb-3 ${textP}`}>Adicionar Cliente</h3>
              {editando && !configs.find(c => c.cliente_id === editando) ? (
                <>
                  <p className={`text-sm mb-3 ${textS}`}>Configurando: <strong>{getClienteNome(editando)}</strong></p>
                  {renderForm()}
                </>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {clientesSemConfig.map(c => (
                    <button key={c.id} onClick={() => editarCliente(c.id)}
                      className={`px-3 py-2 rounded-lg text-sm border ${darkMode ? 'border-zinc-600 hover:bg-zinc-700 text-gray-300' : 'border-gray-300 hover:bg-gray-50 text-gray-700'}`}>
                      + {c.nome}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </Layout>
  );

  function renderForm() {
    const campos = [
      { key: 'custo_marketing_usd', label: 'Marketing (USD)', step: '0.0001' },
      { key: 'custo_utility_usd', label: 'Utility (USD)', step: '0.0001' },
      { key: 'custo_authentication_usd', label: 'Authentication (USD)', step: '0.0001' },
      { key: 'custo_service_usd', label: 'Service (USD)', step: '0.0001' },
      { key: 'taxa_dolar_pct', label: 'Taxa Dólar (%)', step: '0.01' },
      { key: 'iof_pct', label: 'IOF (%)', step: '0.01' },
      { key: 'markup_pct', label: 'Markup (%)', step: '0.01' },
    ];

    return (
      <div className="space-y-4">
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3">
          {campos.map(c => (
            <div key={c.key}>
              <label className={`text-xs ${textS}`}>{c.label}</label>
              <input type="number" step={c.step} value={form[c.key]}
                onChange={e => setForm({ ...form, [c.key]: parseFloat(e.target.value) || 0 })}
                className={inputCls} />
            </div>
          ))}
        </div>
        <div className="flex items-center gap-3">
          <button onClick={salvar} className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm">
            <Save className="w-4 h-4" /> Salvar
          </button>
          <button onClick={() => setEditando(null)} className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm ${darkMode ? 'bg-zinc-700 text-gray-300' : 'bg-gray-100 text-gray-700'}`}>
            <X className="w-4 h-4" /> Cancelar
          </button>
          {cotacao && (
            <span className={`text-sm ${textS}`}>
              Prévia: R$ {(form.custo_marketing_usd * cotacao * (1 + form.taxa_dolar_pct / 100) * (1 + form.iof_pct / 100) * (1 + form.markup_pct / 100)).toFixed(4)} /msg marketing
            </span>
          )}
        </div>
      </div>
    );
  }

  function Campo({ label, valor, destaque }) {
    return (
      <div>
        <p className={`text-xs ${textS}`}>{label}</p>
        <p className={`text-sm font-medium ${destaque ? (darkMode ? 'text-emerald-400' : 'text-emerald-600') : textP}`}>{valor}</p>
      </div>
    );
  }
}
